import React, { useState, useEffect } from 'react';
import { FaChartBar, FaChartLine, FaBullseye } from 'react-icons/fa';

const QuickSightEmbed = ({ setAlert }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardUrl, setDashboardUrl] = useState('');

  useEffect(() => {
    // Simulate dashboard loading
    setTimeout(() => {
      const config = window.pcaSettings;
      setDashboardUrl(config?.dashboard?.uri || '');
      setLoading(false);
    }, 1000);
  }, []);

  const openDashboard = () => {
    if (dashboardUrl) {
      window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
    } else {
      setAlert({
        variant: 'warning',
        heading: 'Dashboard Not Available',
        text: 'Dashboard URL is not configured. Please contact your administrator.'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-1">
          View comprehensive analytics and insights for your call center operations
        </p>
      </div>

      {/* Dashboard Preview */}
      <div className="card text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-lg flex items-center justify-center mb-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          QuickSight Analytics Dashboard
        </h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Access your comprehensive analytics dashboard with real-time insights, trends, and performance metrics.
        </p>
        
        <div className="flex flex-col items-center sm:flex-row sm:justify-center gap-2 sm:gap-4">
        <button
          onClick={openDashboard}
          className="btn-primary"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Open Dashboard
        </button>

        <button className="btn-outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Report
        </button>
        </div>
      </div>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FeatureCard
          icon={<FaChartBar />}
          title="Performance Metrics"
          description="Track key performance indicators, call volumes, and quality scores across all campaigns."
        />
        <FeatureCard
          icon={<FaChartLine />}
          title="Trend Analysis"
          description="Identify patterns and trends in customer satisfaction, agent performance, and call outcomes."
        />
        <FeatureCard
          icon={<FaBullseye />}
          title="Real-time Insights"
          description="Get up-to-the-minute data on ongoing calls, queue status, and system performance."
        />
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="card-hover text-center">
    <div className="text-3xl mb-3">{icon}</div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

export default QuickSightEmbed;